import React, { useRef, useEffect, useContext, useState } from 'react';
import Card from './shared/Card';
import VideoPlayer from './VideoPlayer';
import Toggle from './shared/Toggle';
import Slider from './shared/Slider';
import Button from './shared/Button';
import { ToggleContext } from '../contexts/ToggleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import DownloadButton from './DownloadButton';

const VideoComparison = ({ sourceVideo, processedVideo, playing, onPlayPause }) => {
    const { toggles } = useContext(ToggleContext);

    const sourceVideoRef = useRef(null);
    const processedVideoRef = useRef(null);

    const [currentTime, setCurrentTime] = useState(0); // Slider value
    const [duration, setDuration] = useState(0); // Video duration

    const handlePlayBothClick = () => {
        if (sourceVideoRef.current && processedVideoRef.current) {
            if (!playing) {
                sourceVideoRef.current.play();
                processedVideoRef.current.play();
            } else {
                sourceVideoRef.current.currentTime = currentTime;
                processedVideoRef.current.currentTime = currentTime;
                sourceVideoRef.current.pause();
                processedVideoRef.current.pause();
                setCurrentTime(sourceVideoRef.current.currentTime);
            }
            onPlayPause(); // Notify parent to toggle the `playing` state
        }
    };

    useEffect(() => {
        if (sourceVideoRef.current && processedVideoRef.current && toggles.syncVideos) {
            sourceVideoRef.current.currentTime = currentTime;
            processedVideoRef.current.currentTime = currentTime;
        }
    }, [toggles.syncVideos]);

    const handleSliderChange = (event) => {
        const newTime = parseFloat(event.target.value);
        setCurrentTime(newTime);
        if (sourceVideoRef.current && processedVideoRef.current) {
            sourceVideoRef.current.currentTime = newTime;
            processedVideoRef.current.currentTime = newTime;
        }
    };

    useEffect(() => {
        const updateSlider = () => {
            if (sourceVideoRef.current) {
                setCurrentTime(sourceVideoRef.current.currentTime);
            }
        };

        const setVideoDuration = () => {
            if (sourceVideoRef.current) {
                setDuration(sourceVideoRef.current.duration);
            }
        };

        if (sourceVideoRef.current) {
            sourceVideoRef.current.addEventListener('timeupdate', updateSlider);
            sourceVideoRef.current.addEventListener('loadedmetadata', setVideoDuration);
        }

        return () => {
            if (sourceVideoRef.current) {
                sourceVideoRef.current.removeEventListener('timeupdate', updateSlider);
                sourceVideoRef.current.removeEventListener('loadedmetadata', setVideoDuration);
            }
        };
    }, []);

    const handleVideoEnded = () => {
        if (playing) {
            onPlayPause(); // Stop playback state in the parent
        }
    };

    useEffect(() => {
        if (sourceVideoRef.current) {
            sourceVideoRef.current.addEventListener('ended', handleVideoEnded);
        }
        if (processedVideoRef.current) {
            processedVideoRef.current.addEventListener('ended', handleVideoEnded);
        }

        return () => {
            if (sourceVideoRef.current) {
                sourceVideoRef.current.removeEventListener('ended', handleVideoEnded);
            }
            if (processedVideoRef.current) {
                processedVideoRef.current.removeEventListener('ended', handleVideoEnded);
            }
        };
    }, [playing]);

    const handleDownload = () => {
        if (processedVideo && processedVideo.s3_url) {
            const link = document.createElement('a');
            link.href = processedVideo.s3_url;
            link.setAttribute('download', 'processed_video.mp4');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    console.log("Processed Video: ", processedVideo);

    return (
        <div>
            <div className="flex flex-row lg:gap-8">
                <Card>
                    <div className="flex flex-wrap lg:flex-nowrap lg:gap-8">
                        {sourceVideo && (
                            <VideoPlayer
                                videoUrl={sourceVideo.s3_url}
                                ref={sourceVideoRef}
                                title="Source Video"
                                controls={!toggles.syncVideos}
                            />
                        )}
                    </div>
                </Card>
                <Card>
                    <div className="flex flex-row space-x-4">
                        {processedVideo && (
                            <VideoPlayer
                                videoUrl={processedVideo.s3_url}
                                ref={processedVideoRef}
                                title="Processed Video"
                                controls={!toggles.syncVideos}
                            />
                        )}
                    </div>
                </Card>
            </div>
            <div className="m-4 flex items-center space-x-4">
                {processedVideo && toggles.syncVideos && (
                    <>
                        <Button
                            className="p-2 rounded-full"
                            onClick={handlePlayBothClick}
                        >
                            {playing ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faPlay} />}
                        </Button>
                        <Slider
                            min={0}
                            max={duration}
                            step={0.0001}
                            value={currentTime}
                            onInput={handleSliderChange}
                        />
                    </>
                )}
            </div>
            <div className="m-4 flex justify-center">
                <Toggle id="syncVideos" label="Sync Videos" />
            </div>
            {processedVideo && (
                <div className="flex justify-end">
                    <DownloadButton processedVideoId={processedVideo.id} />
                </div>
            )}
        </div>
    );
};

export default VideoComparison;
